import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { handleForecastPaginatedLastActionSuccess } from 'src/features/forecastPaginated/forecastPaginatedActions/hooks/handleForecastPaginatedLastActionSuccess';
import { useRenameSessionAfterLastAction } from 'src/features/forecastPaginated/forecastPaginatedActions/hooks/useRenameSessionAfterLastAction';
import { useForecastPaginatedUpdate } from 'src/features/forecastPaginated/hooks/useForecastPaginatedUpdate';
import { useForecastSession } from 'src/features/forecastSessions/useForecastSession';
import { useBoundStore } from 'src/store/store';

type PendingLastAction = 'skip' | 'validate' | null;

type UseForecastPaginatedLastActionParams = {
  hasNext: boolean;
  handleNext: () => void;
  isDestocking?: boolean;
  sessionId: string;
  sku: string;
};

export function useForecastPaginatedLastAction({
  hasNext,
  handleNext,
  isDestocking,
  sessionId,
  sku,
}: UseForecastPaginatedLastActionParams) {
  const { t } = useTranslation('forecast');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { sessions } = useForecastSession();
  const setForecastSessionRedirectSuppressed = useBoundStore(
    (state) => state.setForecastSessionRedirectSuppressed,
  );
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingLastAction, setPendingLastAction] =
    useState<PendingLastAction>(null);
  const [requireNextSessionName, setRequireNextSessionName] = useState(false);
  const [nextSessionName, setNextSessionName] = useState('');
  const { updateForecastProposal, skipForecastProposal } =
    useForecastPaginatedUpdate(sessionId, sku, isDestocking);
  const {
    isRenamingSession,
    renameModalOpen,
    nextSessionName: renameSessionName,
    setNextSessionName: setRenameSessionName,
    renameSessionAndNavigateToConfiguration,
    openRenameSession,
    handleCancelRename,
    handleConfirmRename,
  } = useRenameSessionAfterLastAction();

  const closeWarningModal = () => {
    setIsWarningModalOpen(false);
    setPendingLastAction(null);
    setRequireNextSessionName(false);
    setNextSessionName('');
  };

  const finalizeLastAction = (action: Exclude<PendingLastAction, null>) => {
    const previousSessions = [...sessions];
    const requestedNextSessionName = nextSessionName.trim();
    const callback = () => {
      void handleForecastPaginatedLastActionSuccess({
        closeWarningModal,
        navigate,
        openRenameSession,
        previousSessions,
        queryClient,
        renameSessionAndNavigateToConfiguration,
        requestedNextSessionName,
        setForecastSessionRedirectSuppressed,
        t,
      });
    };

    if (action === 'skip') {
      skipForecastProposal(callback);
      return;
    }

    updateForecastProposal(callback);
  };

  const handleActionClick = (
    action: Exclude<PendingLastAction, null>,
    isLastPendingProduct: boolean,
  ) => {
    if (isLastPendingProduct) {
      const isLastOpenSession = sessions.length === 1;
      setPendingLastAction(action);
      setRequireNextSessionName(isLastOpenSession);
      setNextSessionName('');
      setIsWarningModalOpen(true);
      return;
    }

    if (action === 'skip') {
      skipForecastProposal(() => {
        if (hasNext) {
          handleNext();
        }
      });
      return;
    }

    updateForecastProposal(() => {
      if (hasNext) {
        handleNext();
      }
    });
  };

  return {
    confirmLabel: t(pendingLastAction === 'skip' ? 'Skip' : 'Validate'),
    isRenamingSession,
    isWarningModalOpen,
    nextSessionName,
    renameSessionName,
    renameModalOpen,
    requireNextSessionName,
    setNextSessionName,
    setRenameSessionName,
    handleActionClick,
    handleCancelRename,
    handleConfirmLastAction: () => {
      if (!pendingLastAction) {
        return;
      }

      finalizeLastAction(pendingLastAction);
    },
    handleConfirmRename,
    handleDismissWarning: () => {
      closeWarningModal();
      setForecastSessionRedirectSuppressed(false);
    },
  };
}
